import { createRoot } from 'react-dom/client';
import './design/global.css';
import './blocks/blocks.css';
import './start/start.css';
import './signin/signin.css';
import { Toaster } from './blocks/Toast';
import { startTheme } from './design/theme';
import { startPressing } from './blocks/press';
import { SignIn } from './signin/SignIn';
import { lazy, Suspense } from 'react';

// The legal pages carry their texts, and set-up and recovery are rare: loaded only when opened.
const LegalPage = lazy(() => import('./legal/Legal').then((m) => ({ default: m.LegalPage })));
const Setup = lazy(() => import('./signin/Recovery').then((m) => ({ default: m.Setup })));
const Recover = lazy(() => import('./signin/Recovery').then((m) => ({ default: m.Recover })));
import { catchInstallOffer, InstallOffer } from './install/install';
import { reloadOnMissingParts, startWorker, switchOver } from './core/worker';
import { applyLanguage, useLanguage } from './i18n';
import { App, Boundary } from './start/App';
import { UpdateNotice } from './start/UpdateNotice';
import { paintCircle } from './start/bloom';
import { readSession } from './core/session';
import { deleteDatabasesLeft } from './core/device';

// The browser's install offer is kept from the very start (32); the switch-over runs before anything is drawn (06).
catchInstallOffer();
deleteDatabasesLeft(); // a sign-out's, before anything opens a database (03, Signing out)
await switchOver();
// The person's accent is in place before anything is drawn, so the start and the app share its colours (01, 03).
await Promise.all([applyLanguage(), startTheme()]);
paintCircle(readSession()?.user_id); // the circle's colour for the frame's tints (02, Bloom)
startPressing();
startWorker();
reloadOnMissingParts();

/** The whole tree draws again when the language changes (02, Languages: at once, no reload). */
function Root() {
  useLanguage();
  return (
    <Boundary>
      <App
        signedOut={(config, route, signedIn) =>
          route.page === 'login' ? (
            <SignIn key={route.server ?? ''} config={config} server={route.server} onSignedIn={signedIn} />
          ) : (
            <Suspense>
              {route.page === 'legal' && <LegalPage doc={route.doc} signedIn={false} />}
              {route.page === 'setup' && <Setup config={config} server={route.server} />}
              {route.page === 'recover' && <Recover config={config} server={route.server} />}
            </Suspense>
          )
        }
      />
      <Toaster />
      <InstallOffer />
      <UpdateNotice />
    </Boundary>
  );
}

createRoot(document.getElementById('root')!).render(<Root />);
